import axios from 'axios'
import jwtDecode from 'jwt-decode'
import store from '@/store'
import httpChatbot from '@/api/httpChatbot'

// Saved chat history (CAI database, stage 1). The chats live in the MAIN API
// (gi-tests/api, /cai routes), which only ever returns the logged-in user's own
// chats. The chatbot API saves each question + answer there itself; this file
// is only what the chat page needs to list, open, rename and delete them, and
// to ask for consent.
//
// Its own axios instance on purpose: the portal's shared `http` client logs the
// user out on any 401, and a problem with chat history must never do that.
const httpCai = axios.create({
  baseURL: process.env.VUE_APP_API_URL
})

httpCai.interceptors.request.use(config => {
  const token = store.state.USER && store.state.USER.user && store.state.USER.user.token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
}, error => Promise.reject(error))

// Consent (AI-58). Wording v1 (14 Oct 2026). If the wording ever changes,
// bump CONSENT_TEXT_VERSION so the database records which wording each user
// agreed to.
export const CONSENT_KIND = 'save_chat_history'
export const CONSENT_TEXT_VERSION = 'v1'
export const CONSENT_TITLE = 'Save your chats?'
export const CONSENT_TEXT = "Other users can't see them, and you can delete them anytime."

// Is saving switched on in the chatbot (CAI_HISTORY_ENABLED)? The history panel
// and the consent question only appear when it is.
export async function isHistoryEnabled () {
  try {
    const { data } = await httpChatbot.get('/health')
    return !!(data && data.cai_history)
  } catch (err) {
    return false
  }
}

export function listChats () {
  return httpCai.get('cai/conversations', { params: { limit: 50 } })
}

export function getChat (id) {
  return httpCai.get(`cai/conversations/${encodeURIComponent(id)}`)
}

export function renameChat (id, title) {
  return httpCai.patch(`cai/conversations/${encodeURIComponent(id)}`, { title })
}

export function deleteChat (id) {
  return httpCai.delete(`cai/conversations/${encodeURIComponent(id)}`)
}

// Thumbs up (1) / down (-1) on one of CAI's saved answers, with an optional
// comment. Rating again replaces the earlier rating (one per user per answer).
export function giveFeedback (messageId, rating, comment) {
  return httpCai.post(`cai/messages/${encodeURIComponent(messageId)}/feedback`, {
    rating,
    comment: comment ? String(comment).slice(0, 2000) : null
  })
}

export function getConsent () {
  return httpCai.get('cai/consent', { params: { kind: CONSENT_KIND } })
}

export function setConsent (granted) {
  const body = granted
    ? { kind: CONSENT_KIND, granted: true, textVersion: CONSENT_TEXT_VERSION }
    : { kind: CONSENT_KIND, granted: false }
  return httpCai.post('cai/consent', body)
}

// Who is logged in, for remembering in this browser that the consent question
// was already answered (so "No" isn't asked again on every visit). Same order
// as the main API and the chatbot use: id, else email, else organisation.
export function currentUserKey () {
  try {
    const token = store.state.USER.user.token
    const claims = jwtDecode(token)
    return String(claims.id || claims.email || claims.sub || claims.organisation || '')
  } catch (err) {
    return ''
  }
}

// Mirrors the chatbot's context_key_for(): the agent id as the database stores it.
export function contextKeyFor (contextId) {
  let key = String(contextId || '').trim().toLowerCase().replace(/[^a-z0-9_]/g, '_')
  if (key && !/^[a-z]/.test(key)) key = 'c_' + key
  key = key.slice(0, 64)
  return /^[a-z][a-z0-9_]{0,63}$/.test(key) ? key : 'general'
}
